import React, { useState } from 'react';
import { FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { ImageElementType } from '../../../redux/TabTile/TabStateInterfaces';

interface ScalingComponentProps {
  selectedImageId: string | null;
  imageElements: ImageElementType[];
  updateScaling: (id: string, scalingType: string) => void;
  color?: string;
}

const scalingOptions = [
  {
    value: 'none',
    label: 'Normal (Original Size)',
    description: 'Displays the image at its original size without any scaling.',
  },
  {
    value: 'fill',
    label: 'Stretch to Fill',
    description: 'Stretches the image to fill the container, ignoring aspect ratio.',
  },
  {
    value: 'contain',
    label: 'Fit Inside',
    description: 'Scales the image to fit inside the container while maintaining aspect ratio.',
  },
  {
    value: 'cover',
    label: 'Fill and Crop',
    description: 'Scales the image to fill the container completely, cropping if necessary.',
  },
  {
    value: 'scale-down',
    label: 'Shrink to Fit',
    description:
      "Shrinks the image to fit inside the container if it's too large, but won't enlarge it.",
  },
];

const ScalingComponent: React.FC<ScalingComponentProps> = ({
  selectedImageId,
  imageElements,
  updateScaling,
  color,
}) => {
  const selectedImage = imageElements.find((img) => img.id === selectedImageId);
  const currentScaling = selectedImage?.scaling || 'contain';

  // Find the description for the current scaling option
  const currentDescription =
    scalingOptions.find((opt) => opt.value === currentScaling)?.description || '';

  const handleScalingChange = (e: SelectChangeEvent<string>) => {
    if (selectedImageId) {
      updateScaling(selectedImageId, e.target.value);
    }
  };

  return (
    <div style={{ marginTop: 3, paddingLeft: 5 }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="optionDescription" style={{ marginBottom: 4, color: color }}>
        Image Scaling
      </div>
      <FormControl sx={{ width: '94%' }} size="small">
        <Select
          value={currentScaling}
          onChange={handleScalingChange}
          disabled={!selectedImageId}
          variant="outlined"
          sx={{
            fontSize: 12,
            backgroundColor: 'white',
            height: '1.5rem',
            color: '#404040',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2bb9bb',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': { minHeight: 32 },
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                  backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                },
              },
            },
            disableScrollLock: true,
          }}
        >
          {scalingOptions.map(({ value, label }) => (
            <MenuItem key={value} value={value} sx={{ fontSize: 12 }}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Description text below the select */}
      {selectedImageId && (
        <Typography
          variant="caption"
          sx={{
            mx: '0.5rem',
            display: 'block',
            marginTop: '15px',
            color: color,
            fontSize: '12px',
            lineHeight: '1.2',
          }}
        >
          {currentDescription}
        </Typography>
      )}
    </div>
  );
};

export default ScalingComponent;
